import React, { useEffect, useState } from "react";
import { Bell, MessageSquare, Search, MessageCircle, Heart, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CATEGORIES = [
    { key: "messages", label: "New messages", desc: "Someone messages you about a listing", icon: MessageSquare },
    { key: "saved_search", label: "Saved search matches", desc: "A fresh listing matches your saved filters", icon: Search },
    { key: "comments", label: "Post replies", desc: "Someone replies to your community post", icon: MessageCircle },
    { key: "favorites", label: "Listing saves", desc: "A buyer saves one of your listings", icon: Heart },
    { key: "digest", label: "Weekly digest", desc: "A roundup of new matches every 7 days", icon: Newspaper },
];

export default function NotificationPrefs() {
    const [prefs, setPrefs] = useState(null);
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        api.get("/notifications/prefs")
            .then((r) => setPrefs(r.data))
            .catch((e) => toast.error(formatApiError(e)));
    }, []);

    async function toggle(key, next) {
        setSaving(key);
        try {
            const { data } = await api.patch("/notifications/prefs", { [key]: next });
            setPrefs(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(null);
        }
    }

    if (!prefs) return null;

    return (
        <section className="max-w-3xl mx-auto px-4 py-6" data-testid="notification-prefs">
            <div className="flex items-center gap-2 mb-3">
                <Bell className="w-4 h-4 text-hellcat" />
                <div className="label-caps">Notifications</div>
            </div>
            <div className="surface rounded-md divide-y divide-white/5">
                {CATEGORIES.map(({ key, label, desc, icon: Icon }) => {
                    const enabled = !!prefs[key];
                    return (
                        <div key={key} className="flex items-start gap-3 px-4 py-3">
                            <Icon className="w-4 h-4 text-onyx-500 mt-1 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-white">{label}</div>
                                <div className="text-xs text-onyx-500 mt-0.5">{desc}</div>
                            </div>
                            <button
                                type="button"
                                onClick={() => toggle(key, !enabled)}
                                disabled={saving === key}
                                aria-pressed={enabled}
                                data-testid={`notif-toggle-${key}`}
                                className="relative shrink-0"
                                style={{
                                    width: 40,
                                    height: 22,
                                    borderRadius: 999,
                                    background: enabled ? "#E10600" : "#262626",
                                    transition: "background-color 0.15s ease",
                                    opacity: saving === key ? 0.5 : 1,
                                    cursor: saving === key ? "wait" : "pointer",
                                }}
                            >
                                <span
                                    style={{
                                        position: "absolute",
                                        top: 3,
                                        left: enabled ? 21 : 3,
                                        width: 16,
                                        height: 16,
                                        borderRadius: "50%",
                                        background: "#fff",
                                        transition: "left 0.15s ease",
                                    }}
                                />
                            </button>
                        </div>
                    );
                })}
            </div>
            <p className="text-[11px] text-onyx-500 mt-2">
                We send from <span className="text-white">onboarding@resend.dev</span> in dev.
                Unsubscribe links are in every email.
            </p>
        </section>
    );
}
